import { z } from "zod";

/**
 * Header variables split by who they belong to (RFC 0002 §3.4, appendix B; D-031), as `orthos_model::variables`
 * splits them. Drawing variables are drawing data: snapshots carry them and `setVariable` changes them. Author
 * variables (current layer and styles, command defaults) belong to each person in each drawing: the client keeps
 * them, and each intent carries the ones that differ from the drawing's saved values in `env.vars`.
 * `fixtures/contracts/v2/variable-classes.json` holds both sides to the same split.
 */

/** Author variables named one by one in appendix B. */
export const AUTHOR_VARIABLES: readonly string[] = [
  "CLAYER",
  "CECOLOR",
  "CECOLOR_RGB",
  "CELTYPE",
  "CELWEIGHT",
  "CELTSCALE",
  "CETRANSPARENCY",
  "CPLOTSTYLE",
  "CANNOSCALE",
  "TEXTSTYLE",
  "TEXTSIZE",
  "CMLSTYLE",
  "CMLSCALE",
  "CMLJUST",
  "CTABLESTYLE",
  "CMLEADERSTYLE",
  "THICKNESS",
  "ELEVATION",
  "CIRCLERAD",
  "POLYSIDES",
  "DONUTID",
  "DONUTOD",
  "FILLETRAD",
  "CHAMFERA",
  "CHAMFERB",
  "CHAMFERC",
  "CHAMFERD",
  "CHAMMODE",
  "TRIMMODE",
  "EDGEMODE",
  "PROJMODE",
  "TRIMEXTENDMODE",
  "OFFSETDIST",
  "OFFSETERASE",
  "OFFSETLAYER",
  "INSNAME",
  "SPLKNOTS",
  "REVCLOUDARCLEN",
  "ARRAYASSOCIATIVITY",
  "SNAPUNIT",
  "GRIDUNIT",
  "SNAPANG",
  "SNAPBASE",
  "ORTHOS_LAYERP",
  "ORTHOS_LAYISO",
];

/** Author variables by prefix: DIMSTYLE and the DIM* overrides, and the hatch defaults HP*. */
export const AUTHOR_PREFIXES: readonly string[] = ["DIM", "HP"];

/** Drawing variables that an author prefix would otherwise claim. */
export const DRAWING_EXCEPTIONS: readonly string[] = ["DIMASSOC"];

/** Whether a header variable is per person rather than drawing data. */
export function isAuthorVariable(name: string): boolean {
  if (DRAWING_EXCEPTIONS.includes(name)) return false;
  return (
    AUTHOR_VARIABLES.includes(name) || AUTHOR_PREFIXES.some((prefix) => name.startsWith(prefix))
  );
}

/**
 * The next handle: derived from the handles (RFC 0002 §2), so no operation sets it. A snapshot lists it among the
 * drawing variables, as hexadecimal text, only when the drawing has handed out a handle above its largest one.
 */
export const HANDSEED = "HANDSEED";

/** Upper-case letters, digits and underscores, as the engine keys its header. */
export const VARIABLE_NAME = /^[A-Z0-9_]+$/;

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** What a header variable holds: a number, a string (record names as record IDs) or a point. */
export type VariableValue = number | string | Vec3;

export const vec3Schema: z.ZodType<Vec3> = z.strictObject({
  x: z.number(),
  y: z.number(),
  z: z.number(),
});

const variableValueSchema: z.ZodType<VariableValue> = z.union([z.number(), z.string(), vec3Schema]);

/** A drawing's variables: drawing data only (RFC 0002 §10 item 4). */
export type DrawingVariables = Readonly<Record<string, VariableValue>>;

/** The author variables an intent carries (`env.vars`). */
export type AuthorVars = Readonly<Record<string, VariableValue>>;

function variablesSchema(author: boolean): z.ZodType<Readonly<Record<string, VariableValue>>> {
  return z.record(z.string().regex(VARIABLE_NAME), variableValueSchema).superRefine((vars, ctx) => {
    for (const name of Object.keys(vars)) {
      if (isAuthorVariable(name) !== author)
        ctx.addIssue({
          code: "custom",
          message: `${name} is ${author ? "not an author" : "an author"} variable (RFC 0002 appendix B)`,
        });
    }
  });
}

export const drawingVariablesSchema: z.ZodType<DrawingVariables> = variablesSchema(false);
export const authorVarsSchema: z.ZodType<AuthorVars> = variablesSchema(true);

/** The name `setVariable` may change: a drawing variable, never an author one, and never HANDSEED. */
export const drawingVariableNameSchema = z
  .string()
  .regex(VARIABLE_NAME)
  .refine((name) => !isAuthorVariable(name), "author variables never enter the operation log")
  .refine((name) => name !== HANDSEED, "HANDSEED follows the handles; no operation sets it");
